'use client';

import {autoUpdate, computePosition, flip, offset} from '@floating-ui/react';
import {useToggle} from '@rocket-ui/react-tools';
import React, {FC, PropsWithChildren, RefObject, useContext, useEffect, useRef} from 'react';
import ReactDOM from 'react-dom';

interface ContextProps {
	root: RefObject<any>;
	panel: RefObject<any>;
	state: ReturnType<typeof useToggle>;
}

const Context = React.createContext<ContextProps | null>(null);

interface TooltipSubmodules {
	Button: typeof TooltipButton;
	Panel: typeof TooltipPanel;
}

export type TooltipProps = PropsWithChildren;

export const Tooltip: FC<TooltipProps> & TooltipSubmodules = ({children}) => {
	const root = useRef<HTMLDivElement>(null);
	const panel = useRef<HTMLDivElement | null>(null);
	const state = useToggle(false);

	useEffect(() => {
		const div = document.createElement('div');
		div.classList.add('fixed');

		panel.current = div;
	}, []);

	return (
		<div className={'w-full'} ref={root}>
			<Context.Provider value={{root, panel, state}}>{children}</Context.Provider>
		</div>
	);
};

const TooltipButton: FC<PropsWithChildren> = ({children}) => {
	const ctx = useContext(Context);

	if (!ctx) return null;

	const {state} = ctx;

	const handlePointerEnter = () => state.open();

	const handlePointerLeave = () => state.close();

	return (
		<div
			className={'cursor-default pointer-events-auto w-full'}
			onPointerEnter={handlePointerEnter}
			onPointerLeave={handlePointerLeave}
		>
			{children}
		</div>
	);
};

const TooltipPanel: FC<PropsWithChildren> = ({children}) => {
	const ctx = useContext(Context);

	useEffect(() => {
		if (!ctx?.state.opened) return;

		document.body.appendChild(ctx.panel.current);

		return () => {
			document.body.removeChild(ctx.panel.current);
		};
	}, [ctx?.state.opened]);

	useEffect(() => {
		if (!ctx) return;

		const {root, panel, state} = ctx;

		if (!state.opened) return;

		if (!root.current) return;
		if (!panel.current) return;

		const updatePosition = () => {
			if (!root.current) return;
			if (!panel.current) return;

			computePosition(root.current, panel.current, {
				strategy: 'fixed',
				placement: 'right',
				middleware: [offset(40), flip()],
			}).then(({x, y}) => {
				if (!panel.current) return;

				Object.assign(panel.current.style, {
					left: `${x}px`,
					top: `${y}px`,
				});
			});
		};

		return autoUpdate(root.current, panel.current, updatePosition);
	}, [ctx?.state.opened]);

	if (!ctx) return null;

	const {panel} = ctx;

	if (!panel.current) return null;

	return ReactDOM.createPortal(
		<div className={'bg-white/30 backdrop-blur-3xl border border-black/[.04] rounded shadow'}>{children}</div>,
		panel.current,
	);
};

Tooltip.Button = TooltipButton;
Tooltip.Panel = TooltipPanel;
